// src/authProvider.js - Fixed tenant extraction
import { jwtDecode } from 'jwt-decode';
import { extractTenantFromHostname } from './utils/tenant';

// Function to determine the API URL based on current environment and tenant
const getApiUrl = () => {
  // Extract tenant from hostname consistently
  const tenant = extractTenantFromHostname();

  // Read environment from runtime config
  const nodeEnv = window.APP_CONFIG?.NODE_ENV || 'production';

  // When running in development mode, use localhost with port 8080
  if (nodeEnv === 'development') {
    return `http://${tenant}.api.wygc.eu:8080/api`;
  }

  // In production, API URL is based on tenant with api subdomain
  return `https://${tenant}.api.wygc.eu/api`;
};

export const authProvider = {
  login: ({ username, password, otp }) => {
    const apiUrl = getApiUrl();

    // If OTP is provided, use the verify-login endpoint
    if (otp) {
      const verifyUrl = `${apiUrl}/auth/verify-login`;
      console.log(`OTP verification request to: ${verifyUrl}`);

      return fetch(verifyUrl, {
        method: 'POST',
        body: JSON.stringify({ username, otp }),
        headers: new Headers({ 'Content-Type': 'application/json' }),
      })
        .then(response => {
          if (response.status < 200 || response.status >= 300) {
            return response.json().then(data => {
              // Throw error with additional information
              const error = new Error(data.error || response.statusText);
              error.attemptsRemaining = data.attemptsRemaining;
              error.requiresLogin = data.error && data.error.includes('too many failed attempts');
              throw error;
            });
          }
          return response.json();
        })
        .then(({ token }) => {
          // Store JWT token and decoded info
          localStorage.setItem('token', token);
          const decodedToken = jwtDecode(token);
          localStorage.setItem('userId', decodedToken.userId);
          localStorage.setItem('userRole', decodedToken.role);
          localStorage.setItem('userTeamId', decodedToken.team_id);
          localStorage.setItem('username', username);

          // Store tenant from current hostname
          const tenant = extractTenantFromHostname();
          localStorage.setItem('tenant', tenant);
          console.log(`Tenant set to: ${tenant}`);

          return Promise.resolve();
        });
    }

    // Normal login flow
    const loginUrl = `${apiUrl}/auth/login`;
    console.log(`Login request to: ${loginUrl}`);

    return fetch(loginUrl, {
      method: 'POST',
      body: JSON.stringify({ username, password }),
      headers: new Headers({ 'Content-Type': 'application/json' }),
    })
      .then(response => {
        if (response.status < 200 || response.status >= 300) {
          throw new Error(response.statusText);
        }
        return response.json();
      })
      .then((data) => {
        // Check if OTP verification is required
        if (data.requiresVerification) {
          // Throw a special error that will be caught by LoginPage
          const otpError = new Error(data.message || 'OTP verification required');
          otpError.requiresVerification = true;
          otpError.message = data.message;
          otpError.username = data.username || username;
          throw otpError;
        }

        // Normal login success
        const { token } = data;
        localStorage.setItem('token', token);
        const decodedToken = jwtDecode(token);
        localStorage.setItem('userId', decodedToken.userId);
        localStorage.setItem('userRole', decodedToken.role);
        localStorage.setItem('userTeamId', decodedToken.team_id);
        localStorage.setItem('username', username);

        // Store tenant from current hostname
        const tenant = extractTenantFromHostname();
        localStorage.setItem('tenant', tenant);
        console.log(`Tenant set to: ${tenant}`);

        return Promise.resolve();
      });
  },
  
  logout: () => {
    // Save the tenant before clearing localStorage
    const tenant = localStorage.getItem('tenant');
    
    // Clear auth-related items
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userTeamId');
    localStorage.removeItem('username');
    localStorage.removeItem('teamName');
    
    // Keep tenant for subsequent logins
    if (tenant) {
      localStorage.setItem('tenant', tenant);
    }
    
    return Promise.resolve();
  },
  
  checkAuth: () => {
    // Fast synchronous check for token
    const token = localStorage.getItem('token');
    
    if (!token) {
      // Immediately reject if no token found
      return Promise.reject({ redirectTo: '/login', message: false });
    }
    
    // Quick token validation (check if expired)
    try {
      const decodedToken = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      
      if (decodedToken.exp && decodedToken.exp < currentTime) {
        // Token expired
        localStorage.removeItem('token');
        return Promise.reject({ redirectTo: '/login', message: false });
      }
      
      return Promise.resolve();
    } catch (error) {
      // Invalid token
      localStorage.removeItem('token');
      return Promise.reject({ redirectTo: '/login', message: false });
    }
  },
  
  checkError: (error) => {
    if (error && (error.status === 401 || error.status === 403)) {
      localStorage.removeItem('token');
      return Promise.reject();
    }
    return Promise.resolve();
  },
  
  getPermissions: () => {
    const role = localStorage.getItem('userRole');
    return role ? Promise.resolve(role) : Promise.reject();
  },
  
  getIdentity: () => {
    const userId = localStorage.getItem('userId');
    const username = localStorage.getItem('username');
    
    return Promise.resolve({
      id: userId,
      fullName: username || 'User',
    });
  },
};